import {
  format as dateFnsFormat,
  isValid,
  isWeekend,
  isWithinInterval,
  parse,
  parseISO,
} from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

import { getImpossibleFields } from 'directFlow/state/config/dateOfBirth/dateOfBirthValidator.utils'

import { IsoDateFormat } from 'types'
import { CompleteDateObject, DateObject } from 'src/types/date'

const ukTimeFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
})

export const convertToUkDate = (date: Date) => {
  const dateString = ukTimeFormat.format(date)
  const [day, month, year] = dateString.split('/')

  return new Date(Number(year), Number(month) - 1, Number(day))
}

export const getTodayDate = (): Date => {
  const today = convertToUkDate(new Date())
  today.setHours(0, 0, 0, 0)

  return today
}

const formatsByExample = {
  '1 Jan 1970': 'd LLL yyyy',
  'Jan 1, 1970': 'LLL d, yyyy',
  '1st Jan 1970': 'do LLL yyyy',
  '1 January 1970': 'dd MMMM yyy',
  '1st January 1970': 'do MMMM yyyy',
  '1/01/1970': 'dd/MM/yyyy',
  '1970 January 1': 'yyy MMMM dd',
  '1970-01-1': 'yyy-MM-dd',
  '1st January 1970 at 13:00': "do LLLL yyyy 'at' HH:mm",
} satisfies Record<string, string>

type DateFormat = keyof typeof formatsByExample

export const formatDateStr = (date: string | CompleteDateObject, formatExample: DateFormat) => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : convertDateObjectIntoDate(date)

  return formatDate(parsedDate, formatExample)
}

export const formatDate = (date: Date, formatExample: DateFormat) => {
  const format = formatsByExample[formatExample]

  if (!format) {
    return 'Unknown date'
  }

  return dateFnsFormat(date, format)
}

const INTERCOM_WEEKDAY_HOURS = {
  start: 9,
  end: 19,
} as const

const INTERCOM_WEEKEND_HOURS = {
  start: 10,
  end: 16,
} as const

export const isInWorkingHours = () => {
  const currentDateTime = toZonedTime(new Date(), 'Europe/London')

  if (isWeekend(currentDateTime)) {
    const startTime = new Date().setHours(INTERCOM_WEEKEND_HOURS.start, 0, 0, 0)
    const endTime = new Date().setHours(INTERCOM_WEEKEND_HOURS.end, 0, 0, 0)
    if (isWithinInterval(currentDateTime, { start: startTime, end: endTime })) {
      return true
    }
    return false
  }

  const startTime = new Date().setHours(INTERCOM_WEEKDAY_HOURS.start, 0, 0, 0)
  const endTime = new Date().setHours(INTERCOM_WEEKDAY_HOURS.end, 0, 0, 0)

  if (isWithinInterval(currentDateTime, { start: startTime, end: endTime })) {
    return true
  }

  return false
}

export const convertDateObjectIntoDate = (dateObj: CompleteDateObject) => {
  return new Date(Number(dateObj.year), Number(dateObj.month) - 1, Number(dateObj.day))
}

export const convertDateObjIntoIsoString = (dateObj: CompleteDateObject): IsoDateFormat => {
  const date = convertDateObjectIntoDate(dateObj)

  return formatDate(date, '1970-01-1') as IsoDateFormat
}

export const convertIsoDateIntoDateObj = (date: IsoDateFormat): DateObject => {
  const parsedDate = parseISO(date)

  return {
    day: String(parsedDate.getDate()),
    month: String(parsedDate.getMonth() + 1),
    year: String(parsedDate.getFullYear()),
  }
}

export const isCompleteDateObjectType = (
  dateObject: DateObject | undefined | null,
): dateObject is CompleteDateObject => {
  if (!dateObject) {
    return false
  }

  const impossibleValues = getImpossibleFields(dateObject)

  if (impossibleValues.length !== 0) {
    return false
  }

  const date = parse(
    `${dateObject.year}-${dateObject.month}-${dateObject.day}`,
    'yyyy-MM-dd',
    new Date(),
  )

  return isValid(date)
}
